import type { Card, ProductType } from "@/context/AppContext";
import type {
  BundleSelectionProfile,
  BundleTemplate,
  BundleTemplateFilters,
} from "@/types/bundleTemplate";
import {
  PARTIAL_BUILD_THRESHOLD,
  availabilityPctOf,
  type BundleSuggestion,
} from "@/features/bundleEngine";
import {
  buildWeightedProfile,
  pickWeightedBundle,
  type MixOverride,
  type WeightProfileName,
} from "@/features/bundleWeightedPicker";

const MARKET_TARGET_RATIO = 0.8;
const FEE_RATE = 0.13;
const SHIPPING_RESERVE = 1.0;

function totalQty(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.quantity ?? 1), 0);
}

function totalMarket(cards: Card[]): number {
  return cards.reduce(
    (s, c) => s + (c.marketPrice ?? 0) * (c.quantity ?? 1),
    0,
  );
}

function totalCostBasis(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.costBasis ?? 0) * (c.quantity ?? 1), 0);
}

function dominantCondition(cards: Card[]): string {
  const counts: Record<string, number> = {};
  for (const c of cards) {
    counts[c.condition] = (counts[c.condition] ?? 0) + c.quantity;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "NM";
}

function matchesFilters(card: Card, filters: BundleTemplateFilters): boolean {
  if (filters.game && card.game !== filters.game) return false;
  if (filters.sets?.length && !filters.sets.includes(card.setName))
    return false;
  // Rarity + finish are OR-combined when both filters are present (per
  // BundleTemplateFilters JSDoc). When only one is set, that one is the gate.
  const rarityFilter = filters.rarities?.length ? filters.rarities : null;
  const finishFilter = filters.finishes?.length ? filters.finishes : null;
  if (rarityFilter || finishFilter) {
    const rarityHit = rarityFilter ? rarityFilter.includes(card.rarity) : false;
    const finishHit =
      finishFilter && card.finish ? finishFilter.includes(card.finish) : false;
    if (!rarityHit && !finishHit) return false;
  }
  if (
    filters.conditions?.length &&
    !filters.conditions.includes(card.condition)
  )
    return false;
  if (
    filters.maxPricePerCard != null &&
    (card.marketPrice ?? 0) > filters.maxPricePerCard
  ) {
    return false;
  }
  if (
    filters.series?.length &&
    (!card.tcgSeries || !filters.series.includes(card.tcgSeries))
  ) {
    return false;
  }
  if (filters.types?.length) {
    const cardTypes = card.tcgTypes ?? [];
    if (!cardTypes.some((t) => filters.types!.includes(t))) return false;
  }
  if (filters.supertypes?.length) {
    const supertype = (card.tcgSupertype ?? "").toLowerCase();
    const wanted = new Set(filters.supertypes.map((s) => s.toLowerCase()));
    // Card.tcgSupertype values include "Pokémon" — match flexibly.
    const normalized = supertype.replace("é", "e");
    if (!wanted.has(normalized) && !wanted.has(supertype)) {
      // Special handling for "Pokemon" supertype — Cards may store "Pokémon"
      const isPokemonHit = wanted.has("pokemon") && supertype.startsWith("pok");
      if (!isPokemonHit) return false;
    }
  }
  return true;
}

/** Pick a sensible ProductType label for the template's intent. */
function productTypeForTemplate(intent: BundleTemplate["intent"]): ProductType {
  switch (intent) {
    case "Random":
      return "100-card Random Lot";
    case "Set":
      return "Set Builder Lot";
    case "Theme":
      return "50-card Themed Lot";
    case "Decks":
      return "Pre-built Deck";
    case "Trainer/Energy":
      return "25-card Trainer Lot";
  }
}

/**
 * Greedy quantity-cap selection: pick highest-marketPrice cards first, stopping
 * before exceeding `target`. Mirrors random-value-lot behavior so partial
 * inventory still surfaces the most valuable subset.
 */
function pickUpToTarget(pool: Card[], target: number): Card[] {
  const sorted = [...pool].sort(
    (a, b) => (b.marketPrice ?? 0) - (a.marketPrice ?? 0),
  );
  const chosen: Card[] = [];
  let qty = 0;
  for (const c of sorted) {
    if (qty >= target) break;
    if (qty + c.quantity > target) continue;
    chosen.push(c);
    qty += c.quantity;
  }
  // If quantity-cap rejected too many small rows and nothing fit, fall back
  // to the full pool — partial bundles legitimately undershoot target.
  return chosen.length > 0 ? chosen : sorted;
}

/**
 * Fisher-Yates shuffle the pool and greedily fill until target qty.
 * Uses a stable seed derived from the template id + a per-call salt so the
 * same render is consistent but successive refreshes shuffle differently.
 */
function pickShuffled(pool: Card[], target: number, seed: number): Card[] {
  const arr = [...pool];
  // LCG seeded PRNG so the shuffle is reproducible per (template, render).
  let s = seed || 1;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const chosen: Card[] = [];
  let qty = 0;
  for (const c of arr) {
    if (qty >= target) break;
    if (qty + c.quantity > target) continue;
    chosen.push(c);
    qty += c.quantity;
  }
  return chosen.length > 0 ? chosen : arr;
}

function pickViaWeights(
  pool: Card[],
  target: number,
  profileName: WeightProfileName,
  override: MixOverride | undefined,
): Card[] {
  const profile = buildWeightedProfile(profileName, target, override);
  const result = pickWeightedBundle({
    profile,
    profileName,
    // The weighted picker re-applies a profile-level filter (trainer-only,
    // energy-only, etc.) but for bulk/standard/premium it's a no-op so we
    // can hand it our already-filtered pool safely.
    unlistedCards: pool,
  });
  // If the picker undershoots heavily (no inventory satisfied weights), fall
  // back to top-value greedy so the user sees *something*.
  return result.cards.length > 0 ? result.cards : pickUpToTarget(pool, target);
}

function resolveProfile(template: BundleTemplate): BundleSelectionProfile {
  return template.selection?.profile ?? "top-value";
}

type WeightedSelectionProfile = Extract<
  BundleSelectionProfile,
  "bulk" | "standard" | "premium"
>;

function isWeightedProfile(
  p: BundleSelectionProfile,
): p is WeightedSelectionProfile {
  return p === "bulk" || p === "standard" || p === "premium";
}

/**
 * Hash a string to a 32-bit seed. Used to give shuffle a stable per-template
 * seed so the same call site produces the same output within a single render.
 */
function hashSeed(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

export interface GenerateTemplateBundlesOptions {
  /** Per-profile weight tweaks layered on top of BASE_PROFILES. */
  mixOverrides?: Partial<Record<WeightProfileName, MixOverride>>;
  /**
   * Optional extra salt applied to shuffle seeds so callers can force a
   * reshuffle (e.g. "refresh" button). Default 0 = stable within a render.
   */
  shuffleSalt?: number;
}

export function generateTemplateBundles(
  cards: Card[],
  templates: BundleTemplate[],
  usedIds: Set<string>,
  options: GenerateTemplateBundlesOptions = {},
): BundleSuggestion[] {
  if (templates.length === 0) return [];

  const results: BundleSuggestion[] = [];
  const claimed = new Set(usedIds);
  const { mixOverrides, shuffleSalt = 0 } = options;

  for (const template of templates) {
    if (template.targetCount <= 0) continue;
    const pool = cards.filter(
      (c) =>
        !claimed.has(c.id) &&
        c.listingStatus === "Unlisted" &&
        c.quantity > 0 &&
        matchesFilters(c, template.filters),
    );
    if (pool.length === 0) continue;

    const totalAvailable = totalQty(pool);
    const floor = Math.max(
      1,
      Math.floor(template.targetCount * PARTIAL_BUILD_THRESHOLD),
    );
    if (totalAvailable < floor) continue;

    const profile = resolveProfile(template);
    let chosen: Card[];
    if (totalAvailable < template.targetCount) {
      // Partial fill — use everything that survived filtering regardless of
      // the chosen profile. There's nothing to weight against when we're
      // already below target.
      chosen = pool;
    } else if (profile === "shuffle") {
      chosen = pickShuffled(
        pool,
        template.targetCount,
        hashSeed(template.id) + shuffleSalt,
      );
    } else if (isWeightedProfile(profile)) {
      chosen = pickViaWeights(
        pool,
        template.targetCount,
        profile,
        mixOverrides?.[profile],
      );
    } else {
      chosen = pickUpToTarget(pool, template.targetCount);
    }

    const cardCount = totalQty(chosen);
    const market = totalMarket(chosen);
    const cost = totalCostBasis(chosen);
    const expectedSalePrice = market * MARKET_TARGET_RATIO;
    const expectedProfit =
      expectedSalePrice * (1 - FEE_RATE) - cost - SHIPPING_RESERVE;
    const cond = dominantCondition(chosen);
    const productType = productTypeForTemplate(template.intent);
    const availabilityPct = availabilityPctOf(cardCount, template.targetCount);

    results.push({
      id: `user-template-${template.id}`,
      strategyId: "user-template",
      game: chosen[0]?.game ?? "Pokemon",
      bundleType: "User Template",
      label: template.name,
      cards: chosen,
      cardCount,
      estimatedMarketValue: market,
      suggestedProductType: productType,
      suggestedTitle: `${template.name} (${cardCount} cards) – ${cond}/Mixed`,
      confidenceScore: Math.min(95, 40 + Math.floor(availabilityPct / 2)),
      expectedSalePrice,
      estimatedCostBasis: cost,
      expectedProfit,
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: template.targetCount,
      availabilityPct,
    });

    // Reserve picked cards so the next template doesn't double-claim.
    for (const c of chosen) claimed.add(c.id);
  }

  return results;
}
